import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MobileFrame from '@/components/MobileFrame';
import { Icon } from '@/components/Icons';
import { tpl, useT } from '@/lib/i18n';
import { useOnboardingStore, type VerifStatus } from '@/lib/store';
import { getStatus } from '@/lib/onboarding-api';
import { usePeruriErrorTranslator } from '@/lib/peruri-errors';

export default function ReviewScreen() {
    const t = useT();
    const navigate = useNavigate();
    const { formData, phone, verification, certStatus } = useOnboardingStore();
    const setCertStatus = useOnboardingStore((s) => s.setCertStatus);
    const setVerification = useOnboardingStore((s) => s.setVerification);
    const translateErr = usePeruriErrorTranslator();

    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        const poll = async () => {
            try {
                const status = await getStatus();
                setCertStatus(status.statusCode);
                if (status.statusCode === 'CERT_ACTIVE') {
                    setVerification({ registration: 'ok', liveness: 'ok', signature: 'ok' });
                    if (pollRef.current) clearInterval(pollRef.current);
                } else if (status.statusCode === 'CERT_REJECTED') {
                    setErr(translateErr('checkCertificate', status.resultCode, status.resultDesc));
                    if (pollRef.current) clearInterval(pollRef.current);
                }
            } catch (e) {
                // Silent fail, coba lagi di polling berikutnya
                console.warn('poll status failed', e);
            }
        };

        poll();
        pollRef.current = setInterval(poll, 5000);
        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, [setCertStatus, setVerification]);

    const rows: Array<[string, string | null]> = [
        ['NIK', formData.nik],
        ['Nama Lengkap', formData.fullName],
        [
            'Tempat, Tgl Lahir',
            `${formData.birthPlace || '—'}, ${formData.birthDate || '—'}`,
        ],
        [
            'Jenis Kelamin',
            formData.gender === 'M' ? t.genderMale : formData.gender === 'F' ? t.genderFemale : '',
        ],
        ['Email', formData.email],
        ['Alamat', [formData.address, formData.city, formData.province].filter(Boolean).join(', ')],
    ];

    const verifRows: Array<{ label: string; value: string; status: VerifStatus }> = [
        {
            label: t.reviewStatusPhone,
            value: `+62 ${phone}`,
            status: verification.phone,
        },
        {
            label: t.reviewStatusRegistration,
            value: certStatus ?? 'INITIATED',
            status: verification.registration,
        },
        {
            label: t.reviewStatusLiveness,
            value: verification.liveness === 'ok' ? 'Video terverifikasi' : 'Menunggu',
            status: verification.liveness,
        },
        {
            label: t.reviewStatusSignature,
            value: verification.signature === 'ok' ? 'Tersimpan' : 'Menunggu',
            status: verification.signature,
        },
    ];

    const allOk = verifRows.every((r) => r.status === 'ok');
    const canSubmit = allOk && !submitting;

    const handleSubmit = async () => {
        try {
            setSubmitting(true);
            // Re-check final status
            const status = await getStatus();
            setCertStatus(status.statusCode);
            if (status.statusCode === 'CERT_ACTIVE' || status.statusCode === 'EKYC_PENDING') {
                navigate('/onboarding/success');
            } else {
                setErr(translateErr('checkCertificate', status.resultCode, status.resultDesc) || t.somethingWrong);
            }
        } catch (e) {
            setErr(e instanceof Error ? e.message : t.somethingWrong);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <MobileFrame
            progress={(7 / 7) * 100}
            showBack
            stepLabel={tpl(t.stepOf, { current: 7, total: 7 })}
        >
            <div
                className="fade-in"
                style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '8px 24px 28px',
                }}
            >
                <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>
                    {t.reviewTitle}
                </h1>
                <p
                    style={{
                        fontSize: 14,
                        lineHeight: 1.55,
                        color: 'var(--ink-500)',
                        marginBottom: 20,
                    }}
                >
                    {t.reviewSubtitle}
                </p>

                {/* Data card */}
                <div className="card" style={{ padding: 0, marginBottom: 14 }}>
                    <div
                        style={{
                            padding: '14px 16px',
                            borderBottom: '1px solid var(--ink-100)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                        }}
                    >
                        <Icon name="user" size={16} color="var(--primary)" />
                        <span style={{ fontSize: 13, fontWeight: 600 }}>Data Pribadi</span>
                    </div>
                    <div style={{ padding: '4px 16px' }}>
                        {rows.map(([k, v], i) => (
                            <div
                                key={i}
                                style={{
                                    display: 'flex',
                                    padding: '10px 0',
                                    borderBottom:
                                        i < rows.length - 1 ? '1px solid var(--ink-100)' : 'none',
                                    gap: 16,
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: 12,
                                        color: 'var(--ink-500)',
                                        flex: '0 0 38%',
                                    }}
                                >
                                    {k}
                                </div>
                                <div
                                    style={{
                                        fontSize: 13,
                                        fontWeight: 500,
                                        color: 'var(--ink-900)',
                                        flex: 1,
                                        textAlign: 'right',
                                        wordBreak: 'break-word',
                                    }}
                                >
                                    {v || (
                                        <span
                                            style={{
                                                color: 'var(--ink-400)',
                                                fontStyle: 'italic',
                                            }}
                                        >
                                            Kosong
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Verification status */}
                <div className="card" style={{ padding: 0, marginBottom: 16 }}>
                    <div
                        style={{
                            padding: '14px 16px',
                            borderBottom: '1px solid var(--ink-100)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                        }}
                    >
                        <Icon name="shieldCheck" size={16} color="var(--success)" />
                        <span style={{ fontSize: 13, fontWeight: 600 }}>{t.reviewStatusTitle}</span>
                    </div>
                    <div style={{ padding: '4px 16px' }}>
                        {verifRows.map((s, i) => (
                            <div
                                key={i}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 10,
                                    padding: '10px 0',
                                    borderBottom:
                                        i < verifRows.length - 1
                                            ? '1px solid var(--ink-100)'
                                            : 'none',
                                }}
                            >
                                <StatusDot status={s.status} />
                                <div style={{ flex: 1 }}>
                                    <div
                                        style={{
                                            fontSize: 13,
                                            fontWeight: 500,
                                            color: 'var(--ink-900)',
                                        }}
                                    >
                                        {s.label}
                                    </div>
                                    <div style={{ fontSize: 11, color: 'var(--ink-500)' }}>
                                        {s.value}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {err && (
                    <div
                        className="chip chip-error"
                        style={{ marginBottom: 12, alignSelf: 'flex-start' }}
                    >
                        <Icon name="alert" size={14} /> {err}
                    </div>
                )}

                <button
                    type="button"
                    className="btn btn-primary"
                    style={{ width: '100%' }}
                    disabled={!canSubmit}
                    onClick={handleSubmit}
                >
                    {submitting ? (
                        <>
                            <div className="spinner" /> Mengirim...
                        </>
                    ) : (
                        t.reviewSubmit
                    )}
                </button>
            </div>
        </MobileFrame>
    );
}

function StatusDot({ status }: { status: VerifStatus }) {
    const color =
        status === 'ok'
            ? 'var(--success)'
            : status === 'error'
              ? 'var(--error)'
              : 'var(--warning)';
    const icon: 'check' | 'clock' | 'alert' =
        status === 'ok' ? 'check' : status === 'error' ? 'alert' : 'clock';
    return (
        <div
            style={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                background: color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
            }}
        >
            <Icon name={icon} size={12} color="white" strokeWidth={3} />
        </div>
    );
}
